import React from 'react';
import { useForm } from 'react-hook-form';
import { WHATSAPP_NUMBER } from '../constants';

interface FormData {
  name: string;
  phone: string;
  date: string;
  eventType: string;
  message: string;
}

const ContactForm: React.FC = () => {
  const { register, handleSubmit, reset, formState: { errors, touchedFields } } = useForm<FormData>({
    mode: "onChange"
  });

  const onSubmit = (data: FormData) => {
    const text = `Hi, I am interested in your services.%0AName: ${data.name}%0APhone: ${data.phone}%0AEvent: ${data.eventType}%0ADate: ${data.date}%0AMessage: ${data.message}`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, '_blank');
    reset();
  };

  const getBorderClass = (fieldName: keyof FormData) => {
    if (errors[fieldName]) {
      return "border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50";
    }
    if (touchedFields[fieldName] && !errors[fieldName]) {
      return "border-green-500 focus:border-green-500 focus:ring-green-500 bg-green-50";
    }
    return "border-gray-200 focus:border-gold-500 focus:ring-gold-500 bg-gray-50";
  };

  const inputClasses = "w-full px-4 py-3 border rounded-sm focus:outline-none focus:ring-1 transition-all duration-200 text-gray-800 placeholder-gray-400";
  const labelClasses = "block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 ml-1";

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="bg-white p-5 md:p-8 rounded-lg shadow-2xl border-t-4 border-gold-500 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-20 h-20 bg-gold-100 rounded-bl-full -mr-10 -mt-10 opacity-50 pointer-events-none"></div>

      <h3 className="text-2xl font-serif font-bold text-maroon-900 mb-2">Get a Free Quote</h3>
      <p className="text-gray-500 mb-6 text-sm">Tell us about your event and we'll reply instantly on WhatsApp.</p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label className={labelClasses}>Full Name</label>
          <input
            {...register("name", {
              required: "Name is required",
              minLength: { value: 3, message: "Name must be at least 3 characters" }
            })}
            className={`${inputClasses} ${getBorderClass("name")}`}
            placeholder="e.g. Rahul Roy"
          />
          {errors.name && <span className="text-red-500 text-xs mt-1 ml-1 font-medium flex items-center animate-fade-in">
            <span className="mr-1">⚠</span> {errors.name.message}
          </span>}
        </div>

        <div>
          <label className={labelClasses}>Phone Number</label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium border-r border-gray-300 pr-2 mr-2">+91</span>
            <input
              type="tel"
              {...register("phone", {
                required: "Phone number is required",
                pattern: {
                  value: /^[6-9][0-9]{9}$/,
                  message: "Enter valid 10-digit mobile number"
                }
              })}
              className={`${inputClasses} pl-16 ${getBorderClass("phone")}`}
              placeholder="98765 43210"
              maxLength={10}
              onInput={(e) => {
                e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, '');
              }}
            />
          </div>
          {errors.phone ? (
            <span className="text-red-500 text-xs mt-1 ml-1 font-medium flex items-center animate-fade-in">
              <span className="mr-1">⚠</span> {errors.phone.message}
            </span>
          ) : (
            <span className="text-xs text-gray-400 mt-1 ml-1">We will contact you on this number.</span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClasses}>Event Type</label>
            <div className="relative">
              <select
                {...register("eventType", { required: true })}
                className={`${inputClasses} appearance-none cursor-pointer ${getBorderClass("eventType")}`}
              >
                <option value="Wedding">Wedding</option>
                <option value="Reception">Reception</option>
                <option value="Birthday">Birthday</option>
                <option value="Anniversary">Anniversary</option>
                <option value="Corporate">Corporate</option>
                <option value="Other">Other</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
              </div>
            </div>
          </div>
          <div>
            <label className={labelClasses}>Event Date</label>
            <input
              type="date"
              min={today}
              {...register("date", {
                required: "Date is required",
                min: { value: today, message: "Date cannot be in the past" }
              })}
              className={`${inputClasses} cursor-pointer ${getBorderClass("date")}`}
            />
            {errors.date && <span className="text-red-500 text-xs mt-1 ml-1 font-medium animate-fade-in">{errors.date.message}</span>}
          </div>
        </div>

        <div>
          <label className={labelClasses}>Message (Optional)</label>
          <textarea
            {...register("message")}
            className={`${inputClasses} border-gray-200 focus:border-gold-500 focus:ring-gold-500 bg-gray-50`}
            rows={3}
            placeholder="Details about venue, theme, or budget..."
          />
        </div>

        <button
          type="submit"
          className="w-full bg-maroon-900 text-white font-bold py-4 px-6 rounded-sm transition-all shadow-lg hover:shadow-xl hover:bg-maroon-800 active:scale-[0.98] uppercase tracking-widest text-sm flex justify-center items-center gap-2 group"
        >
          <span>Send via WhatsApp</span>
          <svg className="w-4 h-4 fill-current group-hover:translate-x-1 transition-transform" viewBox="0 0 24 24"><path d="M13.293 6.293 7.586 12l5.707 5.707 1.414-1.414L10.414 12l4.293-4.293z" transform="rotate(180 12 12)" /></svg>
        </button>
      </form>
    </div>
  );
};

export default ContactForm;